import { Fragment, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Array } from '@blade/std-lib'
import { SegmentControl } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { Select } from '@root/shared/ui-kit'

import { ColorInput } from './color-input'

import type {
  Appearance,
  ColorVariant,
  PaletteScale,
  Theme as DelugeTheme,
} from '@blade/ui-theme'
import type { TransKey } from '@root/shared/localization'
import type { ClassNameProps } from '@root/shared/react'
import type {
  CustomTheme,
  CustomThemeColors,
  ThemeId,
} from '@root/shared/theme'

type ColorTab = 'base' | 'trend' | 'candle' | 'intents' | 'color' | 'premium'

/**
 * Reference to a palette color used only as preview when the color is not
 * customized yet. `{ variant: 'warning', scale: 9 }` resolves to
 * `theme.palette.warning[9]`.
 */
type PreviewRef = {
  variant: ColorVariant
  scale: keyof PaletteScale
}

type ColorField = {
  key: keyof CustomThemeColors
  labelKey: TransKey
  preview: PreviewRef
}

type FieldItem =
  | { kind: 'appearanceColor' }
  | { kind: 'color'; field: ColorField }

const COLOR_TABS: ReadonlyArray<{
  id: ColorTab
  labelKey: TransKey
  fields: ReadonlyArray<ColorField>
}> = [
  {
    id: 'base',
    labelKey: 'theme.custom.tab.base',
    fields: [
      {
        key: 'primary',
        labelKey: 'theme.custom.primary',
        preview: { variant: 'primary', scale: 9 },
      },
      {
        key: 'background',
        labelKey: 'theme.custom.background',
        preview: { variant: 'background', scale: 7 },
      },
      {
        key: 'text',
        labelKey: 'theme.custom.text',
        preview: { variant: 'text', scale: 7 },
      },
    ],
  },
  {
    id: 'trend',
    labelKey: 'theme.custom.tab.trend',
    fields: [
      {
        key: 'up',
        labelKey: 'theme.custom.up',
        preview: { variant: 'up', scale: 9 },
      },
      {
        key: 'upAccent',
        labelKey: 'theme.custom.upAccent',
        preview: { variant: 'upAccent', scale: 9 },
      },
      {
        key: 'down',
        labelKey: 'theme.custom.down',
        preview: { variant: 'down', scale: 9 },
      },
      {
        key: 'downAccent',
        labelKey: 'theme.custom.downAccent',
        preview: { variant: 'downAccent', scale: 9 },
      },
      {
        key: 'neutral',
        labelKey: 'theme.custom.neutral',
        preview: { variant: 'neutral', scale: 9 },
      },
      {
        key: 'neutralAccent',
        labelKey: 'theme.custom.neutralAccent',
        preview: { variant: 'neutralAccent', scale: 9 },
      },
    ],
  },
  {
    id: 'candle',
    labelKey: 'theme.custom.tab.candle',
    fields: [
      {
        key: 'candleGreen',
        labelKey: 'theme.custom.candleGreen',
        preview: { variant: 'candleGreen', scale: 9 },
      },
      {
        key: 'candleRed',
        labelKey: 'theme.custom.candleRed',
        preview: { variant: 'candleRed', scale: 9 },
      },
      {
        key: 'candleGreenBorder',
        labelKey: 'theme.custom.candleGreenBorder',
        preview: { variant: 'candleGreenBorder', scale: 9 },
      },
      {
        key: 'candleRedBorder',
        labelKey: 'theme.custom.candleRedBorder',
        preview: { variant: 'candleRedBorder', scale: 9 },
      },
      {
        key: 'candleGreenWick',
        labelKey: 'theme.custom.candleGreenWick',
        preview: { variant: 'candleGreenWick', scale: 9 },
      },
      {
        key: 'candleRedWick',
        labelKey: 'theme.custom.candleRedWick',
        preview: { variant: 'candleRedWick', scale: 9 },
      },
    ],
  },
  {
    id: 'intents',
    labelKey: 'theme.custom.tab.intents',
    fields: [
      {
        key: 'warning',
        labelKey: 'theme.custom.warning',
        preview: { variant: 'warning', scale: 9 },
      },
      {
        key: 'warningAccent',
        labelKey: 'theme.custom.warningAccent',
        preview: { variant: 'warning', scale: 9 },
      },
      {
        key: 'success',
        labelKey: 'theme.custom.success',
        preview: { variant: 'success', scale: 9 },
      },
      {
        key: 'successAccent',
        labelKey: 'theme.custom.successAccent',
        preview: { variant: 'successAccent', scale: 9 },
      },
      {
        key: 'error',
        labelKey: 'theme.custom.error',
        preview: { variant: 'error', scale: 9 },
      },
      {
        key: 'errorAccent',
        labelKey: 'theme.custom.errorAccent',
        preview: { variant: 'errorAccent', scale: 9 },
      },
    ],
  },
  {
    id: 'color',
    labelKey: 'theme.custom.tab.color',
    fields: [
      {
        key: 'gray',
        labelKey: 'theme.custom.gray',
        preview: { variant: 'gray', scale: 9 },
      },
      {
        key: 'blue',
        labelKey: 'theme.custom.blue',
        preview: { variant: 'blue', scale: 9 },
      },
      {
        key: 'red',
        labelKey: 'theme.custom.red',
        preview: { variant: 'red', scale: 9 },
      },
      {
        key: 'green',
        labelKey: 'theme.custom.green',
        preview: { variant: 'green', scale: 9 },
      },
      {
        key: 'orange',
        labelKey: 'theme.custom.orange',
        preview: { variant: 'orange', scale: 9 },
      },
      {
        key: 'amber',
        labelKey: 'theme.custom.amber',
        preview: { variant: 'amber', scale: 9 },
      },
      {
        key: 'acid',
        labelKey: 'theme.custom.acid',
        preview: { variant: 'acid', scale: 9 },
      },
      {
        key: 'cyan',
        labelKey: 'theme.custom.cyan',
        preview: { variant: 'cyan', scale: 9 },
      },
      {
        key: 'purple',
        labelKey: 'theme.custom.purple',
        preview: { variant: 'purple', scale: 9 },
      },
      {
        key: 'pink',
        labelKey: 'theme.custom.pink',
        preview: { variant: 'pink', scale: 9 },
      },
      {
        key: 'yellow',
        labelKey: 'theme.custom.yellow',
        preview: { variant: 'yellow', scale: 9 },
      },
      {
        key: 'silver',
        labelKey: 'theme.custom.silver',
        preview: { variant: 'silver', scale: 9 },
      },
      {
        key: 'bronze',
        labelKey: 'theme.custom.bronze',
        preview: { variant: 'bronze', scale: 9 },
      },
      {
        key: 'golden',
        labelKey: 'theme.custom.golden',
        preview: { variant: 'golden', scale: 9 },
      },
    ],
  },
  {
    id: 'premium',
    labelKey: 'theme.custom.tab.premium',
    fields: [
      {
        key: 'subscriptionGradientFrom',
        labelKey: 'theme.custom.subscriptionGradientFrom',
        preview: { variant: 'subscriptionGradientFrom', scale: 9 },
      },
      {
        key: 'subscriptionGradientTo',
        labelKey: 'theme.custom.subscriptionGradientTo',
        preview: { variant: 'subscriptionGradientTo', scale: 9 },
      },
      {
        key: 'subscriptionIcon',
        labelKey: 'theme.custom.subscriptionIcon',
        preview: { variant: 'subscriptionIcon', scale: 9 },
      },
      {
        key: 'subscriptionGlow',
        labelKey: 'theme.custom.subscriptionGlow',
        preview: { variant: 'subscriptionGlow', scale: 9 },
      },
      {
        key: 'subscriptionButton',
        labelKey: 'theme.custom.subscriptionButton',
        preview: { variant: 'subscriptionButton', scale: 9 },
      },
      {
        key: 'boostGradientFrom',
        labelKey: 'theme.custom.boostGradientFrom',
        preview: { variant: 'boostGradientFrom', scale: 9 },
      },
      {
        key: 'boostGradientTo',
        labelKey: 'theme.custom.boostGradientTo',
        preview: { variant: 'boostGradientTo', scale: 9 },
      },
      {
        key: 'boostIcon',
        labelKey: 'theme.custom.boostIcon',
        preview: { variant: 'boostIcon', scale: 9 },
      },
      {
        key: 'boostGlow',
        labelKey: 'theme.custom.boostGlow',
        preview: { variant: 'boostGlow', scale: 9 },
      },
      {
        key: 'boostButton',
        labelKey: 'theme.custom.boostButton',
        preview: { variant: 'boostButton', scale: 9 },
      },
    ],
  },
]

const resolvePreviewColor = (
  theme: DelugeTheme,
  { variant, scale }: PreviewRef,
): string => theme.palette[variant][scale]

export type BaseThemeOption = {
  id: ThemeId
  label: string
}

export type AppearanceOption = {
  id: Appearance
  label: string
}

const APPEARANCE_OPTIONS: ReadonlyArray<Appearance> = ['dark', 'light']

const APPEARANCE_LABEL_KEYS: Record<Appearance, TransKey> = {
  dark: 'theme.custom.appearance.dark',
  light: 'theme.custom.appearance.light',
}

type Props = {
  customTheme: CustomTheme
  baseThemeOptions: ReadonlyArray<BaseThemeOption>
  previewTheme: DelugeTheme
  onAppearanceChange: (appearance: Appearance) => void
  onAppearanceColorChange: (appearanceColor: string) => void
  onBaseThemeChange: (baseTheme: ThemeId) => void
  onChange: (patch: Partial<CustomThemeColors>) => void
  onSubmit: (colors: CustomThemeColors) => void
} & ClassNameProps

export const CustomThemeForm = ({
  customTheme,
  baseThemeOptions,
  previewTheme,
  onAppearanceChange,
  onAppearanceColorChange,
  onBaseThemeChange,
  onChange,
  className,
  onSubmit,
}: Props) => {
  const {
    appearance,
    appearanceColor,
    baseTheme,
    customColors: colors,
  } = customTheme
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<ColorTab>('base')
  const activeFields = useMemo(
    () => COLOR_TABS.find((tab) => tab.id === activeTab)?.fields ?? [],
    [activeTab],
  )
  const fieldRows = useMemo(() => {
    const items: Array<FieldItem> = [
      ...(activeTab === 'base' ? [{ kind: 'appearanceColor' as const }] : []),
      ...activeFields.map((field): FieldItem => ({ kind: 'color', field })),
    ]
    return Array.cluster(2)(items)
  }, [activeFields, activeTab])
  const selectedBaseTheme = useMemo(
    () => baseThemeOptions.find((option) => option.id === baseTheme),
    [baseTheme, baseThemeOptions],
  )
  const appearanceOptions = useMemo<ReadonlyArray<AppearanceOption>>(
    () =>
      APPEARANCE_OPTIONS.map((id) => ({
        id,
        label: t(APPEARANCE_LABEL_KEYS[id]),
      })),
    [t],
  )
  const selectedAppearance = useMemo(
    () => appearanceOptions.find((option) => option.id === appearance),
    [appearanceOptions, appearance],
  )

  return (
    <Root
      className={className}
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit(colors)
      }}
    >
      <Header>
        <BaseThemeField>
          <Select
            label={t('theme.custom.baseTheme')}
            items={baseThemeOptions}
            value={selectedBaseTheme}
            getId={(option) => option.id}
            renderOption={(option) => option.label}
            renderValue={(option) => option.label}
            onChange={(option) => {
              if (option) {
                onBaseThemeChange(option.id)
              }
            }}
          />
        </BaseThemeField>
        <BaseThemeField>
          <Select
            label={t('theme.custom.appearance')}
            items={appearanceOptions}
            value={selectedAppearance}
            getId={(option) => option.id}
            renderOption={(option) => option.label}
            renderValue={(option) => option.label}
            onChange={(option) => {
              if (option) {
                onAppearanceChange(option.id)
              }
            }}
          />
        </BaseThemeField>
      </Header>

      <Tabs size="h36" value={activeTab} onChange={setActiveTab}>
        {COLOR_TABS.map((tab) => (
          <SegmentControl.Item key={tab.id} value={tab.id}>
            {t(tab.labelKey)}
          </SegmentControl.Item>
        ))}
      </Tabs>

      <Fields>
        {fieldRows.map((row, rowIndex) => (
          <Fragment key={rowIndex}>
            {rowIndex > 0 ? <hr /> : undefined}
            <Row>
              {row.map((item) =>
                item.kind === 'appearanceColor' ? (
                  <Field key="appearanceColor">
                    <Label>{t('theme.custom.appearanceColor')}</Label>
                    <ColorInput
                      value={appearanceColor}
                      previewColor={previewTheme.appearanceColor}
                      onChange={onAppearanceColorChange}
                    />
                  </Field>
                ) : (
                  <Field key={item.field.key}>
                    <Label>{t(item.field.labelKey)}</Label>
                    <ColorInput
                      value={colors[item.field.key]}
                      previewColor={resolvePreviewColor(
                        previewTheme,
                        item.field.preview,
                      )}
                      onChange={(value) =>
                        onChange({
                          [item.field.key]: value,
                        } as Partial<CustomThemeColors>)
                      }
                    />
                  </Field>
                ),
              )}
            </Row>
          </Fragment>
        ))}
      </Fields>
    </Root>
  )
}

const Root = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Tabs = styled(SegmentControl<ColorTab>)``

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > hr {
    border: none;
    border-top: 1px solid var(--background-3);
    margin: 0;
  }
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
`

const BaseThemeField = styled.div`
  width: 50%;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Field = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-11);
`
